import type {SketchRelationDefinition} from '../solver/constraint/relationDefinition/SketchRelationDefinition';
import {isSketchPatternedConstraint} from '../solver/constraint/relationDefinition/pattern/SketchPatternedConstraint';
import {isTangentConstraint} from '../solver/constraint/relationDefinition/TangentConstraint';
import {SketchRelationKind} from '../solver/constraint/relation-compatibility/SketchRelationKinds';
import type {GeometryRelationCreationParams} from '../../../types/sketch/solver/constraint/SketchRelationCreationService';
import type {
    SketchPolygonRelationReplacementPort,
    SketchPolygonRelationSyncInput,
} from '../../../types/sketch/polygon/SketchPolygonRelationService';
import type {SketchLineObject, SketchPointObject} from '../../mesh/sketch';

/**
 * 多边形种子线与圆周阵列关系同步服务。
 *
 * 内切圆模式只为构造圆与种子线创建一条相切关系；外接圆模式为每个
 * 唯一多边形顶点创建点—圆重合关系。其余外框边仍只由一个 Patterned
 * 结构关系驱动。本服务不创建几何，只组织新旧关系的原子替换。
 */
export class SketchPolygonRelationService {
    /**
     * 创建多边形关系同步服务。
     *
     * @param relationReplacementPort Viewer 生命周期内唯一的原子关系替换端口。
     */
    public constructor(
        private readonly relationReplacementPort: SketchPolygonRelationReplacementPort,
    ) {}

    /**
     * 同步多边形种子线相切关系与唯一圆周阵列聚合关系。
     *
     * @param input 新旧外框边、构造圆和待绑定阵列关系。
     * @returns 原子替换与统一求解成功时返回 true。
     */
    public syncRelations(input: SketchPolygonRelationSyncInput): boolean {
        const seedLine = input.currentEdges[0];
        if (!seedLine) {
            throw new Error('多边形关系同步缺少种子线。');
        }
        const currentVertices = this.collectUniqueVertices(input.currentEdges);
        const creations: GeometryRelationCreationParams[] = input.shouldCreateTangency
            ? [{relationKind: SketchRelationKind.Tangent, objects: [seedLine, input.constructionCircle]}]
            : currentVertices.map((vertex) => ({
                relationKind: SketchRelationKind.Coincident,
                objects: [vertex, input.constructionCircle],
            }));
        return this.relationReplacementPort.replaceRelationsWithinActiveMutation({
            relationsToRemove: this.collectOwnedRelations(input),
            creations,
            attachments: [input.patternRelationAttachment],
            seedObjects: [input.constructionCircle, seedLine],
        });
    }

    /**
     * 收集本次多边形重建拥有的旧关系。
     *
     * Patterned 关系只要目标包含新旧外框边就属于当前阵列；构造圆与候选边之间
     * 的相切关系、构造圆与候选顶点之间的重合关系都由当前多边形拥有。完整收集
     * 两种模式的旧关系，才能在切换模式或修改边数时执行一次无残留的原子替换。
     *
     * @param input 多边形关系同步输入。
     * @returns 按关系 ID 去重的待移除关系。
     */
    private collectOwnedRelations(input: SketchPolygonRelationSyncInput): readonly SketchRelationDefinition[] {
        const candidateEdges = [...input.previousEdges, ...input.currentEdges];
        const candidateEdgeIds = new Set(candidateEdges.map((edge) => edge.uuid));
        const candidateVertexIds = new Set(
            this.collectUniqueVertices(candidateEdges).map((vertex) => vertex.uuid),
        );
        const relationMap = new Map<number, SketchRelationDefinition>();

        [input.constructionCircle, ...candidateEdges].forEach((owner) => {
            owner.constraints.getRelations().forEach((relation) => {
                if (isSketchPatternedConstraint(relation)) {
                    if (relation.getTargetsSnapshot().some((target) => candidateEdgeIds.has(target.uuid))) {
                        relationMap.set(relation.id, relation);
                    }
                    return;
                }
                if (!isTangentConstraint(relation)) {
                    if (relation.relationType !== SketchRelationKind.Coincident) {
                        return;
                    }
                    const targets = relation.getTargetsSnapshot();
                    const hasCircle = targets.some((target) => target.uuid === input.constructionCircle.uuid);
                    const polygonVertexTargetCount = targets.filter((target) => (
                        candidateVertexIds.has(target.uuid)
                    )).length;
                    if (targets.length === 2 && hasCircle && polygonVertexTargetCount === 1) {
                        relationMap.set(relation.id, relation);
                    }
                    return;
                }
                const targets = relation.getTargetsSnapshot();
                const hasCircle = targets.some((target) => target.uuid === input.constructionCircle.uuid);
                const polygonEdgeTargetCount = targets.filter((target) => candidateEdgeIds.has(target.uuid)).length;
                if (targets.length === 2 && hasCircle && polygonEdgeTargetCount === 1) {
                    relationMap.set(relation.id, relation);
                }
            });
        });
        return [...relationMap.values()];
    }

    /**
     * 按外框边顺序收集多边形的唯一顶点。
     *
     * 多边形使用共享端点闭合，相邻边会重复引用同一个控制点。外接圆模式
     * 每个几何顶点只允许创建一条点—圆重合关系，因此必须按运行时 UUID 去重，
     * 同时保持首次出现顺序，使关系和图形区标记沿轮廓稳定排列。
     *
     * @param edges 当前或历史多边形外框边。
     * @returns 按轮廓顺序排列的唯一控制点。
     */
    private collectUniqueVertices(edges: readonly SketchLineObject[]): readonly SketchPointObject[] {
        const vertexMap = new Map<string, SketchPointObject>();
        edges.forEach((edge) => {
            [edge.startPoint, edge.endPoint].forEach((vertex) => {
                if (!vertexMap.has(vertex.uuid)) {
                    vertexMap.set(vertex.uuid, vertex);
                }
            });
        });
        return [...vertexMap.values()];
    }
}
